import type { LoginRespuesta, UsuarioVista } from './tipos.ts';

const CLAVE_TOKEN = 'convoca_token';
const CLAVE_REFRESH = 'convoca_refresh';
/** Evento que se dispara cuando la sesión murió de verdad (refresh inválido). */
export const EVENTO_SESION_EXPIRADA = 'convoca:sesion-expirada';

// En desarrollo: rutas relativas (proxy de Vite)
// En producción: URL completa del Worker (variable VITE_API_URL en Pages)
const API_BASE = import.meta.env.VITE_API_URL ?? '';

export function leerToken(): string | null {
  return localStorage.getItem(CLAVE_TOKEN);
}

function leerRefresh(): string | null {
  return localStorage.getItem(CLAVE_REFRESH);
}

export function guardarSesion(s: LoginRespuesta): void {
  localStorage.setItem(CLAVE_TOKEN, s.token);
  localStorage.setItem(CLAVE_REFRESH, s.refresh_token);
}

export function borrarSesion(): void {
  localStorage.removeItem(CLAVE_TOKEN);
  localStorage.removeItem(CLAVE_REFRESH);
}

function sesionExpirada(): void {
  borrarSesion();
  window.dispatchEvent(new Event(EVENTO_SESION_EXPIRADA));
}

// Refresh compartido: el access token dura 1 h y el refresh se rota en cada
// uso, así que solo UNA llamada a /api/auth/refresh puede estar en vuelo:
// dentro de la pestaña (promesa compartida) y entre pestañas (Web Locks).
let refrescando: Promise<boolean> | null = null;

async function refrescarSesion(tokenUsado: string | null): Promise<boolean> {
  if (!refrescando) {
    refrescando = (async () => {
      try {
        const correr = () => refrescarDeVerdad(tokenUsado);
        if (typeof navigator !== 'undefined' && navigator.locks) {
          return await navigator.locks.request('convoca_refresh', correr);
        }
        return await correr();
      } finally {
        refrescando = null;
      }
    })();
  }
  return refrescando;
}

async function refrescarDeVerdad(tokenUsado: string | null): Promise<boolean> {
  // Otra pestaña ya rotó mientras esperábamos el lock: reintentar con lo nuevo.
  const actual = leerToken();
  if (actual && actual !== tokenUsado) return true;
  const refresh = leerRefresh();
  if (!refresh) return false;
  try {
    // fetch directo: NO pasa por apiFetch (evita recursión).
    const resp = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (resp.status === 401 || resp.status === 403) {
      // Refresh inválido (revocado/vencido): la sesión murió de verdad.
      sesionExpirada();
      return false;
    }
    // 429 / 5xx: el servidor no pudo ahora; no matar la sesión.
    if (!resp.ok) return false;
    const data = (await resp.json()) as { token: string; refresh_token?: string };
    localStorage.setItem(CLAVE_TOKEN, data.token);
    if (data.refresh_token) localStorage.setItem(CLAVE_REFRESH, data.refresh_token);
    return true;
  } catch {
    // Error de red: no matar la sesión, solo fallar este intento.
    return false;
  }
}

export async function apiFetch<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  return hacerRequest<T>(path, opts, true);
}

async function hacerRequest<T>(
  path: string,
  opts: RequestInit,
  puedeRefrescar: boolean,
): Promise<T> {
  const token = leerToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  const texto = await resp.text();
  const data = texto ? (JSON.parse(texto) as unknown) : null;

  if (!resp.ok) {
    if (resp.status === 401 && token) {
      // Access vencido: renovar en silencio y reintentar UNA vez.
      if (puedeRefrescar && (await refrescarSesion(token))) {
        return hacerRequest<T>(path, opts, false);
      }
      if (!puedeRefrescar) sesionExpirada();
    }
    const mensaje =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Error ${resp.status}`;
    throw new Error(mensaje);
  }
  return data as T;
}

export async function login(email: string, password: string): Promise<UsuarioVista> {
  const resp = await apiFetch<LoginRespuesta>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  guardarSesion(resp);
  return resp.usuario;
}

/** "Ingresar con Google": manda el ID token de Google Identity Services. */
export async function loginGoogle(credential: string): Promise<UsuarioVista> {
  const resp = await apiFetch<LoginRespuesta>('/api/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  });
  guardarSesion(resp);
  return resp.usuario;
}

/** Cierra la sesión en el servidor (revoca el refresh) y limpia el navegador. */
export async function logout(): Promise<void> {
  const token = leerToken();
  const refresh = leerRefresh();
  borrarSesion();
  if (!token && !refresh) return;
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refresh_token: refresh ?? '' }),
    });
  } catch {
    // sin red igual se cierra la sesión local
  }
}
